import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { mockUser, mockUserNoNFT } from '@/mocks/mockUser'
import { DEV_CONFIG, simulateDelay, devLog } from '@/config/dev'
import { verifyPytheniansNFT } from '@/utils/nftVerification'
import { SOLANA_CONFIG } from '@/config/constants'

export const useUserStore = defineStore('user', () => {
  // LocalStorage keys
  // NOTE: User data is now fetched from MongoDB, not stored in localStorage
  const STORAGE_KEYS = {
    WALLET: 'pythentity_wallet',
    AUTH: 'pythentity_auth',
    DISCORD_TOKEN: 'pythentity_discord_token' // TEMPORARY: Until MongoDB is available
  }

  // State
  const currentUser = ref(null)
  const walletConnected = ref(false)
  const walletAddress = ref(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)
  const error = ref(null)

  // Verification state
  const lastVerifiedAt = ref(null)
  const verificationInProgress = ref(false)

  // LocalStorage helpers
  // NOTE: User data is fetched from MongoDB on each login, not cached in localStorage
  function saveToLocalStorage() {
    try {
      if (walletAddress.value) {
        localStorage.setItem(STORAGE_KEYS.WALLET, walletAddress.value)
      }
      localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(isAuthenticated.value))
      devLog('[localStorage] Saved wallet state')
    } catch (err) {
      // Save error
    }
  }

  function loadFromLocalStorage() {
    try {
      const savedWallet = localStorage.getItem(STORAGE_KEYS.WALLET)
      const savedAuth = localStorage.getItem(STORAGE_KEYS.AUTH)

      if (savedWallet) {
        walletAddress.value = savedWallet
      }
      if (savedAuth) {
        isAuthenticated.value = JSON.parse(savedAuth)
      }

      devLog('[localStorage] Auth state restored', savedWallet ? `wallet: ${savedWallet}` : '(no wallet)')
    } catch (err) {
      clearLocalStorage()
    }
  }

  function clearLocalStorage() {
    try {
      localStorage.removeItem(STORAGE_KEYS.WALLET)
      localStorage.removeItem(STORAGE_KEYS.AUTH)
      localStorage.removeItem(STORAGE_KEYS.DISCORD_TOKEN)
      // Also remove legacy key if it exists
      localStorage.removeItem('pythentity_user')
      devLog('[localStorage] Cleared wallet state')
    } catch (err) {
      // Clear error
    }
  }

  // TEMPORARY: Discord token helpers (until MongoDB is available)
  function saveDiscordToken(token) {
    try {
      localStorage.setItem(STORAGE_KEYS.DISCORD_TOKEN, token)
      devLog('[localStorage] Saved Discord token')
    } catch (err) {
      // Failed to save Discord token
    }
  }

  function getDiscordToken() {
    try {
      return localStorage.getItem(STORAGE_KEYS.DISCORD_TOKEN)
    } catch (err) {
      return null
    }
  }

  // Session restore state
  const sessionRestoring = ref(false)

  // Initialize from localStorage on store creation
  loadFromLocalStorage()

  // Getters
  const hasNFT = computed(() => {
    return currentUser.value && currentUser.value.nft !== null
  })

  const isLinkedToDiscord = computed(() => {
    return currentUser.value?.linkedAccounts?.discord?.linked || false
  })

  const isLinkedToTwitter = computed(() => {
    return currentUser.value?.linkedAccounts?.twitter?.linked || false
  })

  const userBadges = computed(() => {
    return currentUser.value?.badges || []
  })

  const userRank = computed(() => {
    return currentUser.value?.rank || null
  })

  const totalScore = computed(() => {
    return currentUser.value?.totalScore || 0
  })

  const isAdmin = computed(() => {
    return currentUser.value?.isAdmin === true
  })

  // Actions
  async function connectWallet(mockHasNFT = true) {
    loading.value = true
    error.value = null

    try {
      devLog('Connecting wallet...')

      if (DEV_CONFIG.useMockData && DEV_CONFIG.mockWalletConnection) {
        await simulateDelay()

        // Mock wallet connection
        const mockWalletAddr = mockHasNFT
          ? mockUser.walletAddress
          : mockUserNoNFT.walletAddress

        walletAddress.value = mockWalletAddr
        walletConnected.value = true

        devLog('Mock wallet connected:', mockWalletAddr)

        // Auto-fetch user data
        await fetchUserData(mockHasNFT)
      } else {
        // Real wallet connection logic
        // TODO: Implement Solana wallet adapter
        throw new Error('Real wallet connection not yet implemented')
      }
    } catch (err) {
      error.value = err.message
      devLog('Wallet connection error:', err)
    } finally {
      loading.value = false
    }
  }

  async function disconnectWallet() {
    walletAddress.value = null
    walletConnected.value = false
    currentUser.value = null
    isAuthenticated.value = false
    error.value = null

    clearLocalStorage()
    devLog('Wallet disconnected')
  }

  async function verifyNFTOwnership() {
    if (!walletAddress.value) {
      throw new Error('No wallet connected')
    }

    devLog('[NFT Check] Verifying NFT ownership for:', walletAddress.value)

    try {
      const result = await verifyPytheniansNFT(
        walletAddress.value,
        SOLANA_CONFIG.RPC_URL
      )

      devLog('[NFT Check] Verification result:', result)

      return result
    } catch (err) {
      devLog('[NFT Check] Verification error:', err)
      throw err
    }
  }

  async function fetchUserData(mockHasNFT = true) {
    if (!walletAddress.value) {
      throw new Error('No wallet connected')
    }

    loading.value = true
    error.value = null

    try {
      devLog('Fetching user data for:', walletAddress.value)

      if (DEV_CONFIG.useMockData) {
        await simulateDelay()

        // Return mock user data
        currentUser.value = mockHasNFT ? mockUser : mockUserNoNFT
        isAuthenticated.value = mockHasNFT

        saveToLocalStorage()
        devLog('Mock user data loaded:', currentUser.value)
      } else {
        // Real NFT verification
        const nftResult = await verifyNFTOwnership()

        if (nftResult.hasNFT) {
          // User has NFT - fetch existing user data from MongoDB or create new profile
          let existingUser = null

          try {
            const response = await fetch(`/.netlify/functions/get-user?wallet=${walletAddress.value}`)
            if (response.ok) {
              const data = await response.json()
              if (data.success && data.user) {
                existingUser = data.user
                devLog('Found existing user in database:', existingUser.wallet)
              }
            }
          } catch (fetchErr) {
            devLog('Could not fetch existing user (may be new user):', fetchErr.message)
          }

          // Build user profile - merge existing data with fresh NFT data
          currentUser.value = {
            walletAddress: walletAddress.value,
            username: existingUser?.username || `Pythian`,
            nft: nftResult.nft, // Always use fresh NFT data
            linkedAccounts: {
              discord: existingUser?.discord ? {
                linked: true,
                id: existingUser.discord.id,
                username: existingUser.discord.username,
                roles: existingUser.discord.roles || [],
                linkedAt: existingUser.discord.linkedAt
              } : { linked: false },
              twitter: existingUser?.twitter ? {
                linked: true,
                handle: existingUser.twitter.username,
                userId: existingUser.twitter.id,
                linkedAt: existingUser.twitter.linkedAt
              } : { linked: false }
            },
            scores: existingUser?.scores || {
              discord: 0,
              twitter: 0,
              pythGovernance: 0,
              pythOIS: 0,
              wStaking: 0,
              jupStaking: 0
            },
            totalScore: existingUser?.totalScore || 0,
            rank: existingUser?.rank || null,
            totalUsers: 3000,
            badges: existingUser?.badges || [],
            isAdmin: existingUser?.isAdmin === true
          }
          isAuthenticated.value = true

          saveToLocalStorage()
          devLog('User data loaded with NFT:', nftResult.nft.name, existingUser ? '(existing user)' : '(new user)')

          // Persist NFT data to MongoDB so restoreSession() can find it
          try {
            await fetch('/.netlify/functions/update-user', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                wallet: walletAddress.value,
                nft: nftResult.nft,
                action: 'updateNFT'
              })
            })
            devLog('[NFT] Persisted NFT data to database')
          } catch (nftSaveErr) {
            devLog('[NFT] Could not persist NFT to database:', nftSaveErr.message)
          }
        } else {
          // No NFT found
          currentUser.value = {
            walletAddress: walletAddress.value,
            nft: null
          }
          isAuthenticated.value = false

          saveToLocalStorage()
          devLog('No NFT found for wallet')
        }
      }
    } catch (err) {
      error.value = err.message
      devLog('Fetch user data error:', err)
    } finally {
      loading.value = false
    }
  }

  async function linkTwitter() {
    loading.value = true
    error.value = null

    try {
      devLog('Initiating Twitter OAuth...')

      if (DEV_CONFIG.useMockData) {
        await simulateDelay(1000)

        // Mock Twitter link
        if (currentUser.value) {
          currentUser.value.linkedAccounts.twitter = {
            linked: true,
            handle: 'mockuser',
            userId: '987654321',
            linkedAt: new Date().toISOString()
          }
        }

        devLog('Twitter account linked (mock)')
      } else {
        // Real Twitter OAuth - redirect to Netlify Function
        devLog('Redirecting to Twitter OAuth...')
        window.location.href = '/.netlify/functions/link-twitter?wallet=' + walletAddress.value
      }
    } catch (err) {
      error.value = err.message
      devLog('Twitter link error:', err)
    } finally {
      loading.value = false
    }
  }

  async function linkDiscord() {
    loading.value = true
    error.value = null

    try {
      devLog('Initiating Discord OAuth...')

      if (DEV_CONFIG.useMockData) {
        await simulateDelay(1000)

        // Mock Discord link
        if (currentUser.value) {
          currentUser.value.linkedAccounts.discord = {
            linked: true,
            username: 'MockUser#1234',
            userId: '123456789',
            roles: ['1234567890', '0987654321'], // Mock role IDs
            linkedAt: new Date().toISOString()
          }
          saveToLocalStorage()
        }

        devLog('Discord account linked (mock)')
      } else {
        // Real Discord OAuth - redirect to Netlify Function
        devLog('Redirecting to Discord OAuth...')
        window.location.href = '/.netlify/functions/link-discord?wallet=' + walletAddress.value
      }
    } catch (err) {
      error.value = err.message
      devLog('Discord link error:', err)
    } finally {
      loading.value = false
    }
  }

  async function unlinkDiscord() {
    loading.value = true
    error.value = null

    try {
      devLog('Unlinking Discord...')

      if (DEV_CONFIG.useMockData) {
        await simulateDelay(500)

        if (currentUser.value) {
          currentUser.value.linkedAccounts.discord = { linked: false }
          saveToLocalStorage()
        }

        devLog('Discord account unlinked (mock)')
      } else {
        const response = await fetch('/.netlify/functions/unlink-discord', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet: walletAddress.value })
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to unlink Discord')
        }

        if (currentUser.value) {
          currentUser.value.linkedAccounts.discord = { linked: false }
          saveToLocalStorage()
        }

        devLog('Discord account unlinked')
      }
    } catch (err) {
      error.value = err.message
      devLog('Discord unlink error:', err)
    } finally {
      loading.value = false
    }
  }

  async function unlinkTwitter() {
    loading.value = true
    error.value = null

    try {
      devLog('Unlinking Twitter...')

      if (DEV_CONFIG.useMockData) {
        await simulateDelay(500)

        if (currentUser.value) {
          currentUser.value.linkedAccounts.twitter = { linked: false }
          saveToLocalStorage()
        }

        devLog('Twitter account unlinked (mock)')
      } else {
        const response = await fetch('/.netlify/functions/unlink-twitter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet: walletAddress.value })
        })

        if (!response.ok) {
          const data = await response.json()
          throw new Error(data.error || 'Failed to unlink Twitter')
        }

        if (currentUser.value) {
          currentUser.value.linkedAccounts.twitter = { linked: false }
          saveToLocalStorage()
        }

        devLog('Twitter account unlinked')
      }
    } catch (err) {
      error.value = err.message
      devLog('Twitter unlink error:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Verifies wallet is actually unlocked by requesting a signature.
   * This is the ONLY reliable way to detect wallet lock status.
   *
   * Behavior:
   * - User rejection (clicked Cancel): Returns false, user stays logged in
   * - Wallet locked/error: Calls handleWalletLocked(), returns false
   *
   * @param {Object} wallet - Wallet adapter instance with signMessage
   * @param {string} walletAddr - Current wallet address
   * @returns {Promise<boolean>} - True if wallet is verified unlocked
   */
  async function verifyWalletUnlocked(wallet, walletAddr) {
    if (verificationInProgress.value) {
      return false // Prevent concurrent verification attempts
    }

    if (!wallet || !walletAddr) {
      return false
    }

    verificationInProgress.value = true

    try {
      const message = new TextEncoder().encode(
        `Pythentity verification\nTimestamp: ${Date.now()}\nWallet: ${walletAddr}`
      )

      await wallet.signMessage(message)

      lastVerifiedAt.value = Date.now()
      devLog('[Wallet Verification] Wallet confirmed unlocked')
      return true

    } catch (err) {
      devLog('[Wallet Verification] Failed:', err.message)

      // Distinguish user rejection from wallet locked
      const isUserRejection = err.message?.includes('User rejected') ||
                              err.message?.includes('rejected')

      if (isUserRejection) {
        // User clicked Cancel - don't logout, just block the action
        return false
      }

      // Wallet is locked or other error - clear state and redirect
      await handleWalletLocked()
      return false

    } finally {
      verificationInProgress.value = false
    }
  }

  /**
   * Handles detected wallet lock - clears all state
   */
  async function handleWalletLocked() {
    devLog('[Wallet Lock] Detected - clearing state')

    // Clear all state
    walletAddress.value = null
    walletConnected.value = false
    currentUser.value = null
    isAuthenticated.value = false
    error.value = null
    lastVerifiedAt.value = null

    // Clear localStorage
    clearLocalStorage()
  }

  /**
   * Restores a session from localStorage without NFT re-verification.
   * Called by router guard on page load / OAuth redirect.
   * Fetches user data from the API using the saved wallet address.
   */
  async function restoreSession() {
    if (!walletAddress.value || !isAuthenticated.value) return
    if (currentUser.value) return // already loaded
    if (sessionRestoring.value) return

    sessionRestoring.value = true
    try {
      devLog('[Session Restore] Restoring session for:', walletAddress.value)

      const response = await fetch(`/.netlify/functions/get-user?wallet=${walletAddress.value}`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.user) {
          currentUser.value = {
            walletAddress: walletAddress.value,
            username: data.user.username || 'Pythian',
            nft: data.user.nft || null,
            linkedAccounts: {
              discord: data.user.discord ? {
                linked: true,
                id: data.user.discord.id,
                username: data.user.discord.username,
                roles: data.user.discord.roles || [],
                linkedAt: data.user.discord.linkedAt
              } : { linked: false },
              twitter: data.user.twitter ? {
                linked: true,
                handle: data.user.twitter.username,
                userId: data.user.twitter.id,
                linkedAt: data.user.twitter.linkedAt
              } : { linked: false }
            },
            scores: data.user.scores || {},
            totalScore: data.user.totalScore || 0,
            rank: data.user.rank || null,
            totalUsers: 3000,
            badges: data.user.badges || [],
            isAdmin: data.user.isAdmin === true
          }
          walletConnected.value = true
          devLog('[Session Restore] Session restored successfully')

          // If NFT data is missing from DB, verify on-chain and persist
          if (!currentUser.value.nft) {
            devLog('[Session Restore] NFT missing from DB, verifying on-chain...')
            try {
              const nftResult = await verifyPytheniansNFT(
                walletAddress.value,
                SOLANA_CONFIG.RPC_URL
              )
              if (nftResult.hasNFT) {
                currentUser.value.nft = nftResult.nft
                devLog('[Session Restore] NFT verified on-chain:', nftResult.nft.name)
                // Persist to DB for future restores
                fetch('/.netlify/functions/update-user', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    wallet: walletAddress.value,
                    nft: nftResult.nft,
                    action: 'updateNFT'
                  })
                }).catch(err => devLog('[Session Restore] Could not persist NFT:', err.message))
              }
            } catch (nftErr) {
              devLog('[Session Restore] NFT verification failed:', nftErr.message)
            }
          }
        }
      }
    } catch (err) {
      // Session restore failed
    } finally {
      sessionRestoring.value = false
    }
  }

  async function refreshDiscordRoles() {
    if (!currentUser.value?.linkedAccounts?.discord?.linked) {
      devLog('[Discord Refresh] Discord not linked, skipping refresh')
      return
    }

    const discordToken = getDiscordToken()
    if (!discordToken) {
      devLog('[Discord Refresh] No token found, skipping refresh')
      return
    }

    try {
      devLog('[Discord Refresh] Refreshing roles...')

      const response = await fetch('/.netlify/functions/refresh-discord-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accessToken: discordToken,
          wallet: walletAddress.value
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update roles in current user
        if (currentUser.value.linkedAccounts.discord) {
          currentUser.value.linkedAccounts.discord.roles = data.roles
          saveToLocalStorage()
          devLog('[Discord Refresh] Roles updated:', data.roles)
        }
      } else if (data.needsReauth) {
        devLog('[Discord Refresh] Token expired, need to re-link Discord')
        // Could show a notification to user to re-link Discord
      }
    } catch (err) {
      // Discord refresh error
    }
  }

  return {
    // State
    currentUser,
    walletConnected,
    walletAddress,
    isAuthenticated,
    loading,
    error,

    // Getters
    hasNFT,
    isLinkedToDiscord,
    isLinkedToTwitter,
    userBadges,
    userRank,
    totalScore,
    isAdmin,

    // Actions
    connectWallet,
    disconnectWallet,
    fetchUserData,
    verifyNFTOwnership,
    linkDiscord,
    linkTwitter,
    unlinkDiscord,
    unlinkTwitter,
    refreshDiscordRoles,
    saveUserState: saveToLocalStorage,
    clearLocalStorage,
    saveDiscordToken,
    getDiscordToken,

    // Session restore
    sessionRestoring,
    restoreSession,

    // Wallet verification
    lastVerifiedAt,
    verificationInProgress,
    verifyWalletUnlocked,
    handleWalletLocked
  }
})
